/**
 * Bundled sample packs — driven by public/samples/manifest.json.
 *
 * The cockpit used to hard-code one sample file. Now every `.atlagm` in
 * public/samples/ is indexed by `node tools/import-research.mjs` and shows up
 * here automatically — which is how the web-research NPC pack (40 NPCs) lands
 * in front of the GM without a code change.
 *
 * GM-only: the list sits behind the GM password, and on the public build the
 * list and every pack are encrypted (tools/lock-samples.ts) — the password
 * decrypts them here (src/persistence/gm-lock.ts). The dev server serves them
 * plain, which `openMaybeLocked` passes through.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { RULE_SYSTEMS, type RuleSystemId } from '@/domain/campaign';
import { parseCampaignFile } from '@/domain/campaign-file';
import { useT } from '@/i18n';
import { replaceCampaign, setToast, useAppState } from '@/state/store';
import { showImportError } from '@/persistence/storage';
import { WrongPasswordError, gmPassword, lockGm, openMaybeLocked, useGmUnlocked } from '@/persistence/gm-lock';
import GmLockGate from './GmLockGate';

interface PackInfo {
  file: string;
  name: string;
  ruleSystem: RuleSystemId;
  summary: string;
  characters: number;
  npcs: number;
  sessions: number;
  maps: number;
}

interface Manifest {
  generatedAt: string;
  packs: PackInfo[];
}

const SAMPLES_BASE = `${import.meta.env.BASE_URL}samples/`;

/** Fetch a samples/ JSON file, decrypting it when the build locked it. */
export async function fetchSampleJson(file: string): Promise<unknown> {
  const response = await fetch(`${SAMPLES_BASE}${file}`);
  if (!response.ok) throw new Error(String(response.status));
  try {
    return await openMaybeLocked(await response.json(), gmPassword());
  } catch (err) {
    // A stale session password (the build changed it): ask again.
    if (err instanceof WrongPasswordError) lockGm();
    throw err;
  }
}

export default function SamplePacks(): ReactElement {
  const t = useT();
  return (
    <GmLockGate id="packs" label={t('samplePacks.title')}>
      <PackList />
    </GmLockGate>
  );
}

function PackList(): ReactElement {
  const t = useT();
  const { campaign, ui } = useAppState();
  const unlocked = useGmUnlocked();
  const [packs, setPacks] = useState<PackInfo[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchSampleJson('manifest.json')
      .then((value) => {
        const manifest = value as Manifest | null;
        if (alive && manifest && Array.isArray(manifest.packs)) setPacks(manifest.packs);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [unlocked]);

  if (packs.length === 0) return <></>;

  const load = async (pack: PackInfo): Promise<void> => {
    if (campaign && !window.confirm(t('samplePacks.confirm', { name: campaign.name }))) return;
    setBusy(pack.file);
    try {
      const parsed = parseCampaignFile(await fetchSampleJson(pack.file));
      replaceCampaign(parsed.campaign);
      setToast('samplePacks.loaded');
    } catch (err) {
      showImportError(err, 'sample.error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card">
      <h2>{t('samplePacks.title')}</h2>
      <p className="muted">{t('samplePacks.intro')}</p>
      <ul className="item-list">
        {packs.map((pack) => (
          <li key={pack.file}>
            <button
              type="button"
              className="list-item"
              disabled={busy === pack.file}
              onClick={() => void load(pack)}
            >
              <span className="item-title">{pack.name}</span>
              <span className="item-meta">
                {RULE_SYSTEMS[pack.ruleSystem].label[ui.locale]} · {pack.npcs} NPC ·{' '}
                {pack.characters} {t('samplePacks.characters')} · {pack.sessions}{' '}
                {t('samplePacks.sessions')}
                {pack.maps > 0 ? ` · ${pack.maps} ${t('samplePacks.maps')}` : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
